import { LeadScoringSource } from "@/components/LeadScoringPopover/LeadScoringPopover";

import { CompanyCategoryResults } from "../../Apollo/schema/get-people";

/** Whether this badge is a company-only score (C1/C2/C3) rather than a verdict. */
export const isCompanyOnlyScore = (
	companyResult: CompanyCategoryResults | null | undefined
): boolean => ["1", "2", "3"].includes(companyResult?.leadScore ?? "");

/** Whether the company's classification itself is a DQ: a competitor, or disqualified. */
const isClassificationDisqualified = (companyResult: CompanyCategoryResults): boolean =>
	companyResult.result === "Competitor" ||
	companyResult.result === "Disqualified" ||
	companyResult.companyCategory === "Disqualified";

/**
 * Whether the badge shows lead scoring's verdict on the company, so its rubric explains it:
 * C1–C3, or a DQ the rubric gave a company whose classification is not itself a DQ.
 */
const isLeadScoringVerdict = (companyResult: CompanyCategoryResults): boolean =>
	isCompanyOnlyScore(companyResult) ||
	(companyResult.leadScore === "Disqualified" && !isClassificationDisqualified(companyResult));

/**
 * What the scoring card under the tracker's company badge explains. A DQ is explained by whoever
 * gave it: the classification's reason for a competitor or a disqualified company, the rubric for
 * a company lead scoring disqualified (PREC-3755: a company un-flagged as a competitor and then
 * disqualified on its employee size read "Un-flagged as competitor by user" under its DQ).
 */
export const getTrackerBadgeScoringSource = (
	companyResult: CompanyCategoryResults
): LeadScoringSource => {
	const { leadScoringBreakdown, leadScoringReasoning, reason } = companyResult;

	return isLeadScoringVerdict(companyResult)
		? { leadScoringBreakdown, overallReasoning: leadScoringReasoning || reason }
		: { overallReasoning: reason };
};
